import clsx from 'clsx'
import { RiTokenSwapFill as SwapIcon } from 'react-icons/ri'
import { TiPlus as AddIcon } from 'react-icons/ti'
import { Link } from 'react-router-dom'
import { AnalyticsVersion } from 'types/analytics'
import { formatPercentChange } from 'utils/analytics/formatPercentChange'
import { formatDollarAmount } from 'utils/formatDollarAmt'

import { SkeletonBar } from '../Skeletons'

type TokenStatisticsProps = {
  version: AnalyticsVersion
  address: string
  tvl: {
    value?: number
    percentageChange?: number
  }
  volume24H: {
    value?: number
    percentageChange?: number
  }
  volume7D: {
    value?: number
    percentageChange?: number
  }
  fees: {
    value?: number
    percentageChange?: number
  }
}

export default function TokenStatistics({ version, address, tvl, volume24H, volume7D, fees }: TokenStatisticsProps) {
  const getTradeLink = () => {
    return `/swap?inputCurrency=${address}`
  }

  const getAddLiquidityLink = () => {
    if (version === 'v2') {
      return `/liquidity/v2/add/${address}`
    }

    return `/liquidity/v3/add/${address}`
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center gap-3 w-full fixed bottom-0 left-0 right-0 z-50 p-4 border-kodiak-border lg:relative lg:border-t-0 lg:p-0 bg-kodiak-bg-backdrop lg:bg-transparent">
        <Link
          to={getTradeLink()}
          className="cursor-pointer flex-1 flex items-center justify-center bg-kodiak-button-primary !text-white font-medium py-1 px-2 rounded-lg hover:bg-kodiak-brand-secondary transition-colors duration-200 disabled:bg-gray-600 disabled:text-gray-400 disabled:cursor-not-allowed min-h-10 !shrink-0"
        >
          <SwapIcon size={18} className="mr-2" />
          Trade
        </Link>
        <Link
          to={getAddLiquidityLink()}
          className="cursor-pointer flex-1 flex items-center justify-center bg-kodiak-button-secondary !text-white font-medium p-2 rounded-lg hover:bg-kodiak-button-subtle transition-colors duration-200 disabled:bg-gray-600 disabled:text-gray-400 disabled:cursor-not-allowed min-h-10 !shrink-0"
        >
          <AddIcon size={18} className="mr-2" />
          Add Liquidity
        </Link>
      </div>

      <div className=" w-full rounded-lg bg-kodiak-bg-floating flex flex-col lg:gap-4 py-5 px-4">
        <span className="text-2xl text-kodiak-text-primary font-medium">Stats</span>

        {/* <div className="flex flex-row gap-4 flex-wrap justify-between lg:flex-col lg:flex-nowrap lg:justify-start"> */}
        <div className="flex flex-row gap-4 flex-wrap justify-between">
          <div className="flex flex-col gap-2">
            <span className="text-md text-kodiak-text-secondary">TVL</span>

            <div className="flex gap-2 items-center min-h-[2.5rem] whitespace-nowrap">
              {tvl.value !== undefined ? (
                <span className="text-3xl text-kodiak-text-primary font-medium">{formatDollarAmount(tvl.value)}</span>
              ) : (
                <SkeletonBar width="32" height="8" />
              )}

              {tvl.percentageChange !== undefined && (
                <span className={clsx('text-md font-medium', formatPercentChange(tvl.percentageChange)?.className)}>
                  {formatPercentChange(tvl.percentageChange)?.text}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 whitespace-nowrap">
            <span className="text-md text-kodiak-text-secondary">24H Fees</span>

            <div className="flex gap-2 items-center min-h-[2.5rem]">
              {fees.value !== undefined ? (
                <span className="text-3xl text-kodiak-text-primary font-medium">{formatDollarAmount(fees.value)}</span>
              ) : (
                <SkeletonBar width="24" height="8" />
              )}

              {fees.percentageChange !== undefined && (
                <span className={clsx('text-md font-medium', formatPercentChange(fees.percentageChange)?.className)}>
                  {formatPercentChange(fees.percentageChange)?.text}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 whitespace-nowrap">
            {/* <div className="flex flex-col gap-2 whitespace-nowrap"> */}
            <span className="text-md text-kodiak-text-secondary">24H Volume</span>

            <div className="flex gap-2 items-center min-h-[2.5rem]">
              {volume24H.value !== undefined ? (
                <span className="text-3xl text-kodiak-text-primary font-medium">
                  {formatDollarAmount(volume24H.value)}
                </span>
              ) : (
                <SkeletonBar width="32" height="8" />
              )}

              {volume24H.percentageChange !== undefined && (
                <span
                  className={clsx('text-md font-medium', formatPercentChange(volume24H.percentageChange)?.className)}
                >
                  {formatPercentChange(volume24H.percentageChange)?.text}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 whitespace-nowrap">
            <span className="text-md text-kodiak-text-secondary">7D Volume</span>

            <div className="flex gap-2 items-center min-h-[2.5rem]">
              {volume7D.value !== undefined ? (
                <span className="text-3xl text-kodiak-text-primary font-medium">
                  {formatDollarAmount(volume7D.value)}
                </span>
              ) : (
                <SkeletonBar width="24" height="8" />
              )}

              {volume7D.percentageChange !== undefined && (
                <span
                  className={clsx('text-md font-medium', formatPercentChange(volume7D.percentageChange)?.className)}
                >
                  {formatPercentChange(volume7D.percentageChange)?.text}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
